import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useUsers, useUserFilters } from '../hooks';
import { useUserColumns, UsersDataTable, CreateUserDialog } from '../components';

export function UsersPage() {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { filters, updateFilter, updateFilters, resetFilters, handleSort } = useUserFilters();

  const { data, isLoading, isError, error } = useUsers(filters);

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = useState<HTMLDivElement | null>(null);
  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const columns = useUserColumns({
    orderBy: filters.orderBy || '',
    order: filters.order || '',
    onSort: handleSort,
    filters,
  });

  if (isError) {
    return (
      <div className="p-4">
        <p className="text-red-500">{error?.message || 'Error loading users'}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b-1">
        <h1 className="text-xl font-semibold">{t('users')}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('attendee', 'get') && (
            <Link to="/app/attendances">
              <Button variant="outline">{t('attendances')}</Button>
            </Link>
          )}
          {hasAccess('role', 'get') && (
            <Link to="/app/roles">
              <Button variant="outline">{t('roles')}</Button>
            </Link>
          )}
          {hasAccess('device', 'get') && (
            <Link to="/app/devices">
              <Button variant="outline">{t('devices')}</Button>
            </Link>
          )}
          {hasAccess('user', 'create') && <CreateUserDialog />}
        </div>
      </div>
      <div className="px-4 pt-4">
        <UsersDataTable
          tableZoom={tableZoom}
          controlsContainer={tableControlsEl}
          data={data?.results || []}
          columns={columns}
          isLoading={isLoading}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          onClearFilters={resetFilters}
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? data?.total ?? 0}
          onPageChange={(val) => updateFilter('page', val)}
          onLimitChange={(val) => updateFilters({ limit: val, page: 0 })}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
